<template>
    <Head title="Incidents & Near Misses" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
            <div class="flex items-center gap-3">
                <div>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Incidents & Near Misses</h1>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Track and manage safety incidents and near misses</p>
                </div>
                <InfoButton title="Incidents & Near Misses Overview" variant="default">
                    <p>
                        <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                        The Incidents & Near Misses page is your central hub for managing all safety, security,
                        operational, and environmental incidents reported across your organization. It provides a
                        comprehensive view of both actual incidents (events that resulted in harm/loss) and near
                        misses (events that could have caused harm but didn't). Use this page to track incident
                        progress through the complete lifecycle: from initial reporting through triage, investigation,
                        corrective action, and closure. Effective incident management helps identify hazards, prevent
                        recurrence, and maintain regulatory compliance.
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                        • <strong>Search:</strong> Quickly find incidents by incident number, title, or description<br>
                        • <strong>Status Filter:</strong> Filter by workflow status - Open (awaiting triage),
                          Triaged, Under Investigation, Investigation Complete, Corrective Action (in progress
                          or review), or Closed<br>
                        • <strong>Type Filter:</strong> Filter by incident category (Safety, Security, Service &
                          Operations, Environmental, HR & Compliance, Other)<br>
                        • <strong>Severity Filter:</strong> Focus on Low, Medium, High, or Critical severity
                          incidents (assigned during triage)<br>
                        • <strong>Report Type Toggle:</strong> Switch between viewing All, Incidents Only, or
                          Near Misses Only<br>
                        • <strong>Location Filter:</strong> View incidents from specific locations or facilities<br>
                        • <strong>SLA Breach Checkbox:</strong> Show only incidents with breached response or
                          investigation SLA timelines<br>
                        • <strong>SLA Breaches Button:</strong> (Managers only) Access dedicated dashboard showing
                          all incidents with SLA violations<br>
                        • <strong>Report Incident:</strong> Click to access the incident reporting form<br>
                        • <strong>Clear Filters:</strong> Reset all filters to view the complete incident list<br>
                        • <strong>View Action:</strong> Click on any incident to see full details, investigation
                          notes, corrective actions, and activity history
                    </p>
                    <p>
                        <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                        • <strong>Color-coded badges</strong> provide instant visual identification:
                          Red badges = Incidents, Yellow badges = Near Misses<br>
                        • <strong>Severity badges</strong> help prioritize response: Critical (red), High (orange),
                          Medium (yellow), Low (green)<br>
                        • <strong>Status badges</strong> show current workflow stage at a glance<br>
                        • <strong>SLA Breach indicators</strong> appear in red below status when response or
                          investigation deadlines are missed - these require immediate attention<br>
                        • <strong>Anonymous reports</strong> show "Anonymous" instead of reporter name to
                          protect identity<br>
                        • Use <strong>multiple filters simultaneously</strong> for precise results (e.g.,
                          "Critical severity + Open status + Specific location")<br>
                        • The <strong>table shows key information</strong>: Incident number, title, type, location,
                          severity, priority, status, and who reported it when<br>
                        • <strong>Near miss reports are valuable</strong> - they help prevent future incidents by
                          identifying hazards before harm occurs<br>
                        • <strong>Regular monitoring</strong> of this page helps ensure timely triage,
                          investigation, and closure of incidents<br>
                        • Click incident numbers or "View" to access full incident details including triage
                          assessment, investigation workspace, corrective actions, and complete audit trail
                    </p>
                </InfoButton>
            </div>
            <div class="flex items-center space-x-3">
                <Link
                    v-if="canManage"
                    :href="route('incidents.sla-breaches')"
                    class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-600 rounded-md font-semibold text-xs text-red-700 dark:text-red-400 uppercase tracking-widest hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    SLA Breaches
                </Link>
                <Link
                    :href="route('incidents.create')"
                    class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Report Incident
                </Link>
            </div>
        </div>

        <!-- Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4 border border-gray-200 dark:border-gray-700">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Search -->
                <input
                    v-model="localFilters.search"
                    type="text"
                    placeholder="Search incidents..."
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @input="debouncedSearch"
                />

                <!-- Status Filter -->
                <select
                    v-model="localFilters.status"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Status</option>
                    <option value="open">Open</option>
                    <option value="triage_completed">Triaged</option>
                    <option value="under_investigation">Under Investigation</option>
                    <option value="investigation_completed">Investigation Complete</option>
                    <option value="correction_in_progress">Corrective Action</option>
                    <option value="closed">Closed</option>
                </select>

                <!-- Type Filter -->
                <select
                    v-model="localFilters.type_id"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Types</option>
                    <option v-for="type in incidentTypes" :key="type.id" :value="type.id">
                        {{ type.name }}
                    </option>
                </select>

                <!-- Severity Filter -->
                <select
                    v-model="localFilters.severity"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Severities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                </select>
            </div>

            <!-- Additional Filters -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <!-- Near Miss Filter -->
                <select
                    v-model="localFilters.is_near_miss"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">Incidents & Near Misses</option>
                    <option value="0">Incidents Only</option>
                    <option value="1">Near Misses Only</option>
                </select>

                <!-- Location Filter -->
                <select
                    v-model="localFilters.location_id"
                    class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                    @change="applyFilters"
                >
                    <option value="">All Locations</option>
                    <option v-for="location in locations" :key="location.id" :value="location.id">
                        {{ location.name }}
                    </option>
                </select>

                <!-- SLA Breach Filter -->
                <label class="flex items-center">
                    <input
                        v-model="localFilters.sla_breached"
                        type="checkbox"
                        class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        @change="applyFilters"
                    />
                    <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">SLA Breached Only</span>
                </label>
            </div>

            <!-- Filter Actions -->
            <div class="flex justify-between items-center mt-4">
                <button
                    @click="clearFilters"
                    class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                    Clear Filters
                </button>
            </div>
        </div>

        <!-- Incidents Table -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                    <tr>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Incident
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Type & Location
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Severity
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Priority
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Reported
                        </th>
                        <th scope="col" class="relative px-6 py-3">
                            <span class="sr-only">Actions</span>
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr v-if="incidents.data.length === 0">
                        <td colspan="7" class="px-6 py-12 text-center">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No incidents found</h3>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by reporting an incident or near miss.</p>
                        </td>
                    </tr>
                    <tr v-for="incident in incidents.data" :key="incident.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <span v-if="incident.is_near_miss" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                        Near Miss
                                    </span>
                                    <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                        Incident
                                    </span>
                                </div>
                                <div class="ml-4">
                                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                                        {{ incident.incident_number }}
                                    </div>
                                    <div class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                        {{ incident.title }}
                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="text-sm text-gray-900 dark:text-white">{{ incident.type?.name }}</div>
                            <div class="text-sm text-gray-500 dark:text-gray-400">{{ incident.location?.name }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span :class="getSeverityClass(incident.severity)">
                                {{ incident.severity }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span v-if="incident.priority" :class="getPriorityClass(incident.priority)">
                                {{ incident.priority }}
                            </span>
                            <span v-else class="text-sm text-gray-400 dark:text-gray-500">N/A</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex flex-col">
                                <span :class="getStatusClass(incident.status)">
                                    {{ incident.status_label }}
                                </span>
                                <span v-if="incident.sla_response_breached || incident.sla_investigation_breached" class="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                    SLA Breach
                                </span>
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            <div>{{ formatDate(incident.reported_at) }}</div>
                            <div v-if="!incident.is_anonymous && incident.reported_by" class="text-xs text-gray-400 dark:text-gray-500">
                                by {{ incident.reported_by.full_name }}
                            </div>
                            <div v-else class="text-xs text-gray-400 dark:text-gray-500">
                                Anonymous
                            </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                                :href="route('incidents.show', incident.id)"
                                class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                            >
                                View
                            </Link>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Pagination -->
            <div v-if="incidents.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{ incidents.from }}</span>
                            to
                            <span class="font-medium">{{ incidents.to }}</span>
                            of
                            <span class="font-medium">{{ incidents.total }}</span>
                            results
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    incidents: Object,
    filters: Object,
    incidentTypes: Array,
    locations: Array,
    canManage: Boolean,
});

// Use timezone composable for date formatting
const { formatDateTime: formatDateTimeInTZ } = useTimezone();

const localFilters = ref({
    search: props.filters.search || '',
    status: props.filters.status || '',
    type_id: props.filters.type_id || '',
    severity: props.filters.severity || '',
    is_near_miss: props.filters.is_near_miss || '',
    location_id: props.filters.location_id || '',
    sla_breached: props.filters.sla_breached || false,
});

let debounceTimer = null;

const debouncedSearch = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        applyFilters();
    }, 500);
};

const applyFilters = () => {
    router.get(route('incidents.index'), localFilters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    localFilters.value = {
        search: '',
        status: '',
        type_id: '',
        severity: '',
        is_near_miss: '',
        location_id: '',
        sla_breached: false,
    };
    applyFilters();
};

const getSeverityClass = (severity) => {
    const classes = {
        low: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        medium: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        high: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        critical: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[severity] || classes.medium;
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        normal: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        medium: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        high: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        urgent: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        critical: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[priority] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
};

const getStatusClass = (status) => {
    const classes = {
        open: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        triage_completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300',
        under_investigation: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        investigation_completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300',
        correction_in_progress: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        closed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    };
    return classes[status] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getStatusLabel = (status) => {
    const labels = {
        open: 'Open',
        triage_completed: 'Triaged',
        under_investigation: 'Under Investigation',
        investigation_completed: 'Investigation Complete',
        correction_in_progress: 'Corrective Action',
        closed: 'Closed',
    };
    return labels[status] || status;
};

// Format date in company timezone
const formatDate = (date) => {
    if (!date) return 'N/A';
    return formatDateTimeInTZ(date); // Now timezone-aware using company timezone!
};
</script>
